// ZakupyTopCitiesTable.js
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import * as XLSX from "xlsx";
import { FaFileExcel } from "react-icons/fa";
import { Tooltip as InfoTooltip } from "react-tooltip";
import { Info } from "lucide-react";

const Wrapper = styled.div`
  width: 100%;
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
  flex-grow: 1;
  max-width: 300px;
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #1d6f42;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #155e36;
  }

  svg {
    margin-right: 6px;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #ccc;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 12px;
  background-color: #fff;
  font-size: 0.7rem;

  th, td {
    border: 1px solid #ddd;
    text-align: center;
    padding: 14px 8px;
    line-height: 1.6;
  }

  th {
    background: #d5b6ec;
  }

  tr:nth-child(even) {
    background-color: #f9f9f9;
  }

  tr:hover {
    background-color: #f5e8ff;
  }

  .positive {
    background-color: #e7f9ed;
    color: #2e7d32;
    font-weight: 500;
  }

  .negative {
    background-color: #ffeaea;
    color: #c62828;
    font-weight: 500;
  }

  .highlight-top {
    background-color: rgba(0, 128, 0, 0.1);
    font-weight: bold;
  }

  .highlight-low {
    background-color: rgba(255, 0, 0, 0.1);
    font-weight: bold;
  }

  .highlight-neutral {
    background-color: #f0f0f0;
  }

  .rank-up {
    color: green;
    font-weight: bold;
  }

  .rank-down {
    color: red;
    font-weight: bold;
  }

  .rank-neutral {
    color: #666;
  }
`;

const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;

  &:hover {
    color: #000;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const formatPLN = (value) =>
  new Intl.NumberFormat("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const ZakupyTopCitiesTable = ({ purchaseData = [], clientsData = [] }) => {
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [search, setSearch] = useState("");
  const [tableData, setTableData] = useState([]);


useEffect(() => {
  if (!purchaseData.length || !clientsData.length) return;

  const years = [...new Set(purchaseData.map((r) => parseInt(r.Rok)))].sort((a, b) => b - a);
  const defaultYear = years[0];
  const months = purchaseData
    .filter((r) => parseInt(r.Rok) === defaultYear)
    .map((r) => parseInt(r.Miesiac));
  const sortedMonths = [...new Set(months)].sort((a, b) => b - a);
  const defaultMonth = sortedMonths[0] ?? 0;

  if (!selectedYear) setSelectedYear(defaultYear);
  if (selectedMonth === null) setSelectedMonth(defaultMonth);
}, [purchaseData, clientsData]);


// REPLACE the whole 2nd useEffect (that builds tableData) with this version
useEffect(() => {
  if (!selectedYear || selectedMonth === null) return;

  const includeFullYear = selectedMonth === 0;

  // map NIP -> city
  const nipToCity = {};
  clientsData.forEach((c) => {
    if (c.nip?.trim()) {
      nipToCity[c.nip.trim()] = c.miejscowosc || "Nieznane miasto";
    }
  });

  const prevMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
  const prevYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear;
  const yearAgo = selectedYear - 1;

  // group purchases by city
  const cities = {};
  purchaseData.forEach((row) => {
    const rok = parseInt(row.Rok);
    const miesiac = parseInt(row.Miesiac);
    const nip = row.NrDostawcy?.trim();
    const // ✅ use K_42 + K_43 as purchase amount
      amount = (Number(row.K_42) || 0) + (Number(row.K_43) || 0);
    const city = nipToCity[nip] || "Nieznane miasto";

    if (!cities[city]) cities[city] = { city, purchases: [] };
    cities[city].purchases.push({ rok, miesiac, amount });
  });

  // compute metrics for ALL cities (no search filter here)
  const resultsAll = Object.entries(cities)
    .map(([city, data]) => {
      const rows = data.purchases;

      const current = rows
        .filter(r => r.rok === selectedYear && (includeFullYear || r.miesiac === selectedMonth))
        .reduce((sum, r) => sum + r.amount, 0);

      const prev = includeFullYear
        ? 0
        : rows
            .filter(r => r.rok === prevYear && r.miesiac === prevMonth)
            .reduce((sum, r) => sum + r.amount, 0);

      const yoy = rows
        .filter(r => r.rok === yearAgo && (includeFullYear || r.miesiac === selectedMonth))
        .reduce((sum, r) => sum + r.amount, 0);

      const changeMM = includeFullYear || prev === 0 ? null : ((current - prev) / prev) * 100;
      const changeYY = yoy === 0 ? null : ((current - yoy) / yoy) * 100;

      return { city, current, prev, yoy, changeMM, changeYY };
    })
    // exclude "Nieznane miasto" from the ranking base
    .filter(r => r.city !== "Nieznane miasto");

  // ranks based on FULL set (stable regardless of search)
  const rankedNowAll = [...resultsAll].sort((a, b) => b.current - a.current);
  const rankMap = new Map(rankedNowAll.map((r, i) => [r.city, i + 1]));

  const rankedPrevAll = [...resultsAll].sort((a, b) => b.yoy - a.yoy);
  const prevRankMap = new Map(rankedPrevAll.map((r, i) => [r.city, i + 1]));

  const finalAll = resultsAll.map(r => {
    const rank = rankMap.get(r.city) ?? null;
    const prevRank = prevRankMap.get(r.city) ?? null;
    const rankChange = (prevRank && rank) ? (prevRank - rank) : null;
    return { ...r, rank, prevRank, rankChange };
  });

  // take true Top 10 by current-period rank
  const top10 = [...finalAll].sort((a, b) => (a.rank ?? 1e9) - (b.rank ?? 1e9)).slice(0, 10);

  // apply search ONLY after ranks are fixed (search just hides rows)
  const filtered = top10.filter(r =>
    r.city.toLowerCase().includes((search || "").toLowerCase())
  );

  // ensure visible rows are sorted by rank
  setTableData(filtered.sort((a, b) => (a.rank ?? 1e9) - (b.rank ?? 1e9)));
}, [selectedYear, selectedMonth, purchaseData, clientsData, search]);

  const exportToExcel = () => {
    const rows = tableData.map((r) => ({
      Miasto: r.city,
      "Wartość (PLN)": r.current.toFixed(2),
      "m/m-1": r.prev.toFixed(2),
      "% m/m-1": r.changeMM === null ? "—" : `${r.changeMM.toFixed(2)}%`,
      "r/r-1": r.yoy.toFixed(2),
      "% r/r-1": r.changeYY === null ? "—" : `${r.changeYY.toFixed(2)}%`,
      "Ranking": r.rank || "-",
      "Ranking r-1": r.prevRank || "-",
      "Zmiana rankingu": r.rankChange !== null 
        ? (r.rankChange > 0 
          ? `↑ ${r.rankChange}` 
          : r.rankChange < 0 
            ? `↓ ${Math.abs(r.rankChange)}` 
            : "–") : "-"
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "TopCitiesZakupy");
    XLSX.writeFile(wb, "top_miasta_zakupy.xlsx");
  };

  const getMonthName = (num) =>
    new Date(2000, num - 1).toLocaleString("pl-PL", { month: "long" });

  const availableMonths = [...new Set(
    purchaseData.filter((r) => parseInt(r.Rok) === selectedYear).map((r) => parseInt(r.Miesiac))
  )].sort((a, b) => b - a);

  const availableYears = [...new Set(purchaseData.map((r) => parseInt(r.Rok)))].sort((a, b) => b - a);

  return (
    <Wrapper>
      <ChartTitle>
        Zakupy wg miast
        <InfoIconWrapper data-tooltip-id="top-cities-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>
      <InfoTooltip
        id="top-cities-tooltip"
        place="left"
        content={
          <>
            <strong>Ranking miast (zakupy)</strong>
            <br />
            Tabela pokazuje 10 miast o największych zakupach,
            zmiany m/m, r/r oraz pozycje w rankingu.
          </>
        }
      />

      <Controls>
        <Select value={selectedYear || ""} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
          <option value="">Rok</option>
          {availableYears.map((y) => <option key={y} value={y}>{y}</option>)}
        </Select>
        <Select value={selectedMonth ?? ""} onChange={(e) => setSelectedMonth(parseInt(e.target.value))}>
          <option value={0}>Cały rok</option>
          {availableMonths.map((m) => (
            <option key={m} value={m}>{getMonthName(m)}</option>
          ))}
        </Select>
        <SearchInput
          type="text"
          placeholder="Szukaj miasta..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <ExportButton onClick={exportToExcel}>
          <FaFileExcel />
          Eksportuj do Excel
        </ExportButton>
      </Controls>

      <div style={{ overflowX: "auto", width: "100%" }}>
        <StyledTable>
          <thead>
            <tr>
              <th>Miasto</th>
              <th>Wartość (PLN)</th>
              <th>m/m-1</th>
              <th>% m/m-1</th>
              <th>r/r-1</th>
              <th>% r/r-1</th>
              <th>Ranking</th>
              <th>Ranking r-1</th>
              <th>Zmiana rankingu</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((row, idx) => (
              <tr key={idx}>
                <td className="bold">{row.city}</td>
                <td>{formatPLN(row.current)}</td>
                <td>{formatPLN(row.prev)}</td>
                <td className={`bold ${row.changeMM > 0 ? "positive" : row.changeMM < 0 ? "negative" : ""}`}>
                  {row.changeMM === null ? "—" : `${row.changeMM.toFixed(2)}%`}
                </td>
                <td>{formatPLN(row.yoy)}</td>
                <td className={`bold ${row.changeYY > 0 ? "positive" : row.changeYY < 0 ? "negative" : ""}`}>
                  {row.changeYY === null ? "—" : `${row.changeYY.toFixed(2)}%`}
                </td>
                <td className={
                  row.rank <= 3 ? "highlight-top" :
                  row.rank >= 8 ? "highlight-low" : "highlight-neutral"
                }>{row.rank}</td>
                <td className={
                  row.prevRank <= 3 ? "highlight-top" :
                  row.prevRank >= 8 ? "highlight-low" : "highlight-neutral"
                }>{row.prevRank || "-"}</td>
                <td className={
                  row.rankChange > 0 ? "rank-up" :
                  row.rankChange < 0 ? "rank-down" : "rank-neutral"
                }>
                  {row.rankChange !== null
                    ? row.rankChange > 0 ? `↑ ${row.rankChange}` :
                      row.rankChange < 0 ? `↓ ${Math.abs(row.rankChange)}` : "–"
                    : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </div>
    </Wrapper>
  );
};

export default ZakupyTopCitiesTable;
